import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CirclePause } from 'lucide-react';
import { Button, Input, Sheet } from '@/components/ui';
import { formatCurrency } from '@/utils/formatters';
import { CustomerAutocomplete } from './CustomerAutocomplete';
import type { CartController } from './useCartController';

interface Props { cart: CartController; isOpen: boolean; onClose: () => void }

/** Hold-cart form (customer name / phone / notes) — logic lives in the controller. */
export const HoldCartSheet: React.FC<Props> = ({ cart, isOpen, onClose }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        setLoading(true);
        const ok = await cart.hold({ customerName: name, customerPhone: phone, notes });
        setLoading(false);
        if (ok) { setName(''); setPhone(''); setNotes(''); onClose(); }
    };

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title={t('cart.holdCart')}
            footer={
                <div className="flex gap-2.5">
                    <Button variant="ghost" size="lg" fullWidth onClick={onClose} disabled={loading}>{t('cart.cancel')}</Button>
                    <Button variant="primary" size="lg" fullWidth icon={<CirclePause />} onClick={submit} loading={loading}>{t('cart.holdCart')}</Button>
                </div>
            }
        >
            <div className="flex flex-col gap-3">
                <p className="text-sm text-ink-secondary">{t('cart.holdCartTitle')}</p>
                <Input label={t('cart.customerName')} placeholder={cart.customerName || t('cart.enterCustomerName')} value={name} onChange={(e) => setName(e.target.value)} />
                <Input label={t('cart.customerPhone')} type="tel" placeholder={cart.customerPhone || t('cart.enterCustomerPhone')} value={phone} onChange={(e) => setPhone(e.target.value)} />
                <Input label={t('cart.notes')} placeholder={cart.notes || t('cart.addNotes')} value={notes} onChange={(e) => setNotes(e.target.value)} />
                <div className="flex items-center justify-between rounded-md bg-surface-sunken px-4 py-3 text-sm">
                    <span className="text-ink-secondary">{t('cart.itemsCount', { count: cart.itemCount })}</span>
                    <span className="text-lg font-black tnum text-ink-brand">{cart.calculation ? formatCurrency(cart.calculation.grandTotal) : '—'}</span>
                </div>
            </div>
        </Sheet>
    );
};

export const ClearCartSheet: React.FC<Props> = ({ cart, isOpen, onClose }) => {
    const { t } = useTranslation();
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title={t('cart.clearCart')}
            size="sm"
            footer={
                <div className="flex gap-2.5">
                    <Button variant="ghost" size="lg" fullWidth onClick={onClose}>{t('cart.cancel')}</Button>
                    <Button variant="danger" size="lg" fullWidth onClick={() => { cart.clear(); onClose(); }}>{t('cart.confirm')}</Button>
                </div>
            }
        >
            <p className="text-sm text-ink-secondary">{t('cart.clearCartConfirm')}</p>
        </Sheet>
    );
};

/** Customer name / phone / order notes. Writes straight to the cart slice. */
export const CustomerSheet: React.FC<Props> = ({ cart, isOpen, onClose }) => {
    const { t } = useTranslation();
    if (!cart.shopId) return null;
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title={cart.requireCustomerInfo ? t('cart.customerInfoRequired') : t('cart.customerData')}
            footer={<Button variant="primary" size="lg" fullWidth onClick={onClose}>{t('cart.save')}</Button>}
        >
            <div className="flex flex-col gap-3">
                <CustomerAutocomplete shopId={cart.shopId} name={cart.customerName} phone={cart.customerPhone} required={cart.requireCustomerInfo} onChange={cart.setCustomer} />
                <Input label={t('cart.notes')} placeholder={t('cart.addNotes')} value={cart.notes} onChange={(e) => cart.setNotes(e.target.value)} />
            </div>
        </Sheet>
    );
};
